import { Alert, DatePicker, Input, Modal, message, Select } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { Coins, RefreshCcw, Search } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { ADMIN_PERMISSIONS } from '@/auth/permissions';
import { type Column, DataTable } from '@/components/common/DataTable';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  type AdminCommerceCreditAssetViewItem,
  type AdminCommerceCreditHistoryViewItem,
  adminCommerceStore,
  authStore,
  brandStore,
} from '@/stores';
import {
  creditAssetTypeLabel,
  creditChangeTypeLabel,
  creditOperationLabel,
  creditStatusLabel,
} from './creditLabels';

interface AdjustFormState {
  operationType: 'GRANT' | 'DEDUCT';
  credits: string;
  effectiveAt: Dayjs | null;
  expireAt: Dayjs | null;
  reason: string;
  idempotencyKey: string;
}

const emptyAdjustForm = (): AdjustFormState => ({
  operationType: 'GRANT',
  credits: '',
  effectiveAt: null,
  expireAt: null,
  reason: '',
  idempotencyKey:
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}`,
});

function formatDisplayTime(value: string | number | undefined) {
  if (!value) {
    return '-';
  }
  const date =
    typeof value === 'number' ? new Date(value * 1000) : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return dayjs(date).format('YYYY-MM-DD HH:mm:ss');
}

function statusBadge(status: string) {
  const normalized = status.toUpperCase();
  const variant = normalized === 'DONE' ? 'default' : 'outline';
  return <Badge variant={variant}>{creditStatusLabel(normalized)}</Badge>;
}

function handleCreditError(error: unknown, fallback: string) {
  const status =
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof error.response === 'object' &&
    error.response !== null &&
    'status' in error.response
      ? error.response.status
      : undefined;
  if (status === 403) {
    adminCommerceStore.clearCredits();
    void authStore.checkAdminAccess(true);
    message.error('当前账号没有执行该操作的权限');
    return;
  }
  message.error(fallback);
}

export const AdminCommerceCreditsPage = observer(() => {
  const [userIdInput, setUserIdInput] = useState('');
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustForm, setAdjustForm] =
    useState<AdjustFormState>(emptyAdjustForm);
  const activeBrand = brandStore.activeBrand;
  const activeAppId = brandStore.activeBrandAppId;
  const searchedUserId = adminCommerceStore.searchedUserId;
  const canRead = authStore.can(ADMIN_PERMISSIONS.commerceCreditsRead);
  const canWrite = authStore.can(ADMIN_PERMISSIONS.commerceCreditsWrite);

  // biome-ignore lint/correctness/useExhaustiveDependencies: page state must reset when the active brand app changes.
  useEffect(() => {
    adminCommerceStore.clearCredits();
    setUserIdInput('');
  }, [activeAppId]);

  useEffect(
    () => () => {
      adminCommerceStore.clearCredits();
    },
    [],
  );

  useEffect(() => {
    if (!canRead) {
      adminCommerceStore.clearCredits();
    }
  }, [canRead]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: MobX observable filters and pagination drive this refresh.
  useEffect(() => {
    if (!canRead || !searchedUserId) {
      return;
    }
    void adminCommerceStore.loadCreditHistories().catch((error) => {
      console.error('加载积分流水失败:', error);
      handleCreditError(error, '加载积分流水失败');
    });
  }, [
    searchedUserId,
    adminCommerceStore.creditHistoryPagination.page,
    adminCommerceStore.creditHistoryPagination.pageSize,
    adminCommerceStore.creditHistoryFilters.changeType,
    adminCommerceStore.creditHistoryFilters.source,
    adminCommerceStore.creditHistoryFilters.status,
    adminCommerceStore.creditHistoryFilters.startTime,
    adminCommerceStore.creditHistoryFilters.endTime,
    canRead,
  ]);

  const loadCredits = async () => {
    if (!canRead) {
      message.warning('当前账号没有读取积分数据的权限');
      return;
    }
    const userId = userIdInput.trim();
    if (!/^[1-9]\d*$/.test(userId)) {
      message.warning('请输入正整数 User ID');
      return;
    }
    try {
      await adminCommerceStore.loadCredits(userId);
    } catch (error) {
      console.error('加载积分信息失败:', error);
      handleCreditError(error, '加载积分信息失败');
    }
  };

  const openAdjustDialog = () => {
    if (!canWrite) {
      message.warning('当前账号没有调整积分的权限');
      return;
    }
    if (!searchedUserId) {
      message.warning('请先查询用户');
      return;
    }
    setAdjustForm(emptyAdjustForm());
    setAdjustOpen(true);
  };

  const submitAdjust = async () => {
    if (!canWrite || !searchedUserId) {
      return;
    }
    const credits = adjustForm.credits.trim();
    const reason = adjustForm.reason.trim();
    if (!credits || Number(credits) <= 0) {
      message.warning('积分数量必须大于 0');
      return;
    }
    if (!reason) {
      message.warning('操作原因必填');
      return;
    }
    try {
      await adminCommerceStore.adjustCredits({
        userId: searchedUserId,
        operationType: adjustForm.operationType,
        credits,
        reason,
        idempotencyKey: adjustForm.idempotencyKey.trim(),
        effectiveAt: adjustForm.effectiveAt?.toISOString(),
        expireAt: adjustForm.expireAt?.toISOString(),
      });
      message.success('积分调整已提交');
      setAdjustOpen(false);
    } catch (error) {
      console.error('积分调整失败:', error);
      handleCreditError(error, '积分调整失败');
    }
  };

  const assetColumns = useMemo<Column<AdminCommerceCreditAssetViewItem>[]>(
    () => [
      {
        id: 'assetsType',
        header: '资产类型',
        accessor: (row) => creditAssetTypeLabel(row.assetsType),
      },
      {
        id: 'remainingCredits',
        header: '剩余积分',
        accessor: (row) => row.remainingCredits || '0',
      },
      {
        id: 'initialCredits',
        header: '初始积分',
        accessor: (row) => row.initialCredits || '0',
      },
      {
        id: 'effectiveAt',
        header: '生效时间',
        accessor: (row) => formatDisplayTime(row.effectiveAt),
      },
      {
        id: 'expireAt',
        header: '过期时间',
        accessor: (row) => formatDisplayTime(row.expireAt),
      },
    ],
    [],
  );

  const historyColumns = useMemo<Column<AdminCommerceCreditHistoryViewItem>[]>(
    () => [
      {
        id: 'historyNo',
        header: '流水号',
        accessor: (row) => <span className="font-mono">{row.historyNo}</span>,
      },
      {
        id: 'changeCredits',
        header: '变动积分',
        accessor: (row) => (
          <div className="flex items-center gap-2">
            <span>
              {row.totalChangeCreditsForGroup || row.changeCredits || '0'}
            </span>
            {row.recordCount && row.recordCount > 1 ? (
              <Badge variant="outline">{row.recordCount} 条</Badge>
            ) : null}
          </div>
        ),
      },
      {
        id: 'changeType',
        header: '变动类型',
        accessor: (row) => creditChangeTypeLabel(row.changeType),
      },
      {
        id: 'source',
        header: '来源',
        accessor: (row) => row.source || '-',
      },
      {
        id: 'model',
        header: '模型',
        accessor: (row) => row.publicModel || row.upstreamModel || '-',
      },
      {
        id: 'status',
        header: '状态',
        accessor: (row) => statusBadge(row.status),
      },
      {
        id: 'title',
        header: '标题',
        accessor: (row) => row.title || '-',
      },
      {
        id: 'createdAt',
        header: '创建时间',
        accessor: (row) =>
          formatDisplayTime(row.firstCreatedAt || row.createdAt),
      },
      {
        id: 'reason',
        header: '原因',
        accessor: (row) => row.reason || '-',
      },
    ],
    [],
  );

  const overview = adminCommerceStore.creditOverview;

  return (
    <div className="rounded-lg border border-border bg-white shadow-sm">
      <div className="space-y-6 p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-lg font-semibold text-foreground">
                积分管理
              </h1>
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                {activeBrand?.name || '未选择 Brand'}
              </span>
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-mono text-slate-700">
                {activeAppId || '-'}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              disabled={!canRead || !searchedUserId}
              onClick={() => void adminCommerceStore.loadCredits()}
            >
              <RefreshCcw className="mr-2 h-4 w-4" />
              刷新
            </Button>
            {canWrite ? (
              <Button disabled={!searchedUserId} onClick={openAdjustDialog}>
                <Coins className="mr-2 h-4 w-4" />
                调整积分
              </Button>
            ) : null}
          </div>
        </div>

        {!canRead ? (
          <Alert
            type="info"
            showIcon
            message="当前账号仅可打开积分管理页面，没有读取积分数据的权限。"
          />
        ) : null}

        <div className={canRead ? 'flex flex-wrap items-end gap-3' : 'hidden'}>
          <div className="space-y-1">
            <label
              htmlFor="admin-commerce-user-id"
              className="text-sm font-medium"
            >
              User ID
            </label>
            <Input
              id="admin-commerce-user-id"
              inputMode="numeric"
              disabled={!canRead}
              value={userIdInput}
              onChange={(event) => setUserIdInput(event.target.value)}
              className="w-60"
              onPressEnter={() => void loadCredits()}
            />
          </div>
          <Button
            disabled={!canRead}
            loading={adminCommerceStore.creditsLoading}
            onClick={() => void loadCredits()}
          >
            <Search className="mr-2 h-4 w-4" />
            查询
          </Button>
          <Select
            className="w-36"
            value={adminCommerceStore.creditHistoryFilters.status || 'all'}
            onChange={(value) =>
              adminCommerceStore.setCreditHistoryFilters({
                status: value === 'all' ? '' : value,
              })
            }
            options={[
              { value: 'all', label: '全部状态' },
              { value: 'PENDING', label: creditStatusLabel('PENDING') },
              { value: 'DONE', label: creditStatusLabel('DONE') },
              { value: 'FAILED', label: creditStatusLabel('FAILED') },
              { value: 'REVERSED', label: creditStatusLabel('REVERSED') },
            ]}
          />
          <Select
            className="w-36"
            value={adminCommerceStore.creditHistoryFilters.changeType || 'all'}
            onChange={(value) =>
              adminCommerceStore.setCreditHistoryFilters({
                changeType: value === 'all' ? '' : value,
              })
            }
            options={[
              { value: 'all', label: '全部类型' },
              { value: 'CONSUME', label: creditChangeTypeLabel('CONSUME') },
              { value: 'GRANT', label: creditChangeTypeLabel('GRANT') },
              { value: 'RETURN', label: creditChangeTypeLabel('RETURN') },
              { value: 'EXPIRE', label: creditChangeTypeLabel('EXPIRE') },
            ]}
          />
        </div>

        <div
          className={
            canRead ? 'grid gap-4 md:grid-cols-2 xl:grid-cols-4' : 'hidden'
          }
        >
          <Metric label="可用积分" value={overview?.availableCredits || '-'} />
          <Metric
            label="24h 内过期"
            value={overview?.expiringCreditsWithin24h || '-'}
          />
          <Metric
            label="下一次生效"
            value={formatDisplayTime(overview?.nextEffectiveAt)}
          />
          <Metric
            label="下一次过期"
            value={formatDisplayTime(overview?.nextExpireAt)}
          />
        </div>

        <div
          className={
            canRead ? 'grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]' : 'hidden'
          }
        >
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-foreground">积分资产</h2>
            <DataTable
              columns={assetColumns}
              data={adminCommerceStore.creditAssets}
              loading={adminCommerceStore.creditsLoading}
              emptyMessage="暂无积分资产"
            />
          </div>
          <div className="rounded-lg border border-border p-4">
            <h2 className="text-sm font-semibold text-foreground">类型汇总</h2>
            <div className="mt-3 space-y-2">
              {(overview?.creditsByType || []).map((item) => (
                <div
                  key={item.assetsType}
                  className="flex items-center justify-between gap-3 text-sm"
                >
                  <span className="text-slate-500">
                    {creditAssetTypeLabel(item.assetsType)}
                  </span>
                  <span className="font-mono font-medium">
                    {item.availableCredits}
                  </span>
                </div>
              ))}
              {(!overview || overview.creditsByType.length === 0) && (
                <div className="text-sm text-muted-foreground">暂无数据</div>
              )}
            </div>
          </div>
        </div>

        <div className={canRead ? 'space-y-3' : 'hidden'}>
          <h2 className="text-sm font-semibold text-foreground">积分流水</h2>
          <DataTable
            columns={historyColumns}
            data={adminCommerceStore.creditHistories}
            loading={adminCommerceStore.historiesLoading}
            emptyMessage="暂无积分流水"
          />
          {adminCommerceStore.creditHistoryNextCursor ? (
            <div className="mt-2 text-center">
              <Button
                variant="outline"
                size="sm"
                loading={adminCommerceStore.historiesLoading}
                onClick={() => {
                  void adminCommerceStore
                    .loadMoreCreditHistories()
                    .catch((error) => {
                      console.error('加载更多积分流水失败:', error);
                      handleCreditError(error, '加载更多积分流水失败');
                    });
                }}
              >
                加载更多
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      <Modal
        title="调整积分"
        open={canWrite && adjustOpen}
        okText="提交"
        cancelText="取消"
        confirmLoading={adminCommerceStore.adjusting}
        onCancel={() => setAdjustOpen(false)}
        onOk={() => void submitAdjust()}
      >
        <div className="space-y-4 py-2">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label
                htmlFor="admin-commerce-adjust-op"
                className="text-sm font-medium"
              >
                操作类型
              </label>
              <Select
                id="admin-commerce-adjust-op"
                className="w-full"
                value={adjustForm.operationType}
                onChange={(operationType) =>
                  setAdjustForm((current) => ({ ...current, operationType }))
                }
                options={[
                  { value: 'GRANT', label: creditOperationLabel('GRANT') },
                  { value: 'DEDUCT', label: creditOperationLabel('DEDUCT') },
                ]}
              />
            </div>
            <div className="space-y-1">
              <label
                htmlFor="admin-commerce-adjust-credits"
                className="text-sm font-medium"
              >
                积分数量
              </label>
              <Input
                id="admin-commerce-adjust-credits"
                value={adjustForm.credits}
                onChange={(event) =>
                  setAdjustForm((current) => ({
                    ...current,
                    credits: event.target.value,
                  }))
                }
              />
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <DateField
              id="admin-commerce-adjust-effective-at"
              label="生效时间"
              value={adjustForm.effectiveAt}
              onChange={(effectiveAt) =>
                setAdjustForm((current) => ({ ...current, effectiveAt }))
              }
            />
            <DateField
              id="admin-commerce-adjust-expire-at"
              label="过期时间"
              value={adjustForm.expireAt}
              onChange={(expireAt) =>
                setAdjustForm((current) => ({ ...current, expireAt }))
              }
            />
          </div>
          <div className="space-y-1">
            <label
              htmlFor="admin-commerce-adjust-reason"
              className="text-sm font-medium"
            >
              操作原因
            </label>
            <Input.TextArea
              id="admin-commerce-adjust-reason"
              value={adjustForm.reason}
              onChange={(event) =>
                setAdjustForm((current) => ({
                  ...current,
                  reason: event.target.value,
                }))
              }
              rows={3}
            />
          </div>
          <div className="space-y-1">
            <label
              htmlFor="admin-commerce-adjust-key"
              className="text-sm font-medium"
            >
              Idempotency Key
            </label>
            <Input
              id="admin-commerce-adjust-key"
              value={adjustForm.idempotencyKey}
              onChange={(event) =>
                setAdjustForm((current) => ({
                  ...current,
                  idempotencyKey: event.target.value,
                }))
              }
            />
          </div>
        </div>
      </Modal>
    </div>
  );
});

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border p-4">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="mt-2 break-words text-lg font-semibold text-foreground">
        {value}
      </div>
    </div>
  );
}

function DateField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: Dayjs | null;
  onChange: (value: Dayjs | null) => void;
}) {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      <DatePicker
        id={id}
        showTime
        className="w-full"
        value={value}
        onChange={onChange}
      />
    </div>
  );
}
